import { LoadError } from '@/components/LoadError';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useRevokeOtherSessions, useRevokeSession, useSessions } from '@/lib/queries';

/** Короткая подпись устройства: разбор user-agent ненадёжен, поэтому строка
 *  просто укорачивается, а полная остаётся в подсказке. */
function shortDevice(userAgent: string | null): string | null {
  if (!userAgent) return null;
  return userAgent.length > 48 ? `${userAgent.slice(0, 48)}…` : userAgent;
}

/**
 * Экран активных сессий — единственный способ вовремя заметить чужой доступ
 * (docs/SECURITY.md, раздел 12).
 */
export function SessionsPanel() {
  const { t, i18n } = useTranslation();
  const sessions = useSessions();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();

  if (sessions.isError) return <LoadError retry={() => sessions.refetch()} />;
  if (!sessions.data) return <p className="text-sm text-muted">{t('common.loading')}</p>;

  const others = sessions.data.filter((session) => !session.current);

  return (
    <section className="flex flex-col gap-3" aria-label={t('auth.sessions')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-ink-2">{t('auth.sessions')}</h2>
        {others.length > 0 && (
          <Button size="sm" onClick={() => revokeOthers.mutate()}>
            {t('auth.revokeOthers')}
          </Button>
        )}
      </div>

      {(revoke.isError || revokeOthers.isError) && (
        <p role="alert" className="text-sm text-muted">
          {t('week.saveFailed')}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {sessions.data.map((session) => (
          <li
            key={session.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-line bg-surface px-4 py-3"
          >
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-2 text-sm">
                {/* Полная строка user-agent нечитаема; целиком она остаётся
                    в подсказке, чтобы можно было сверить устройство. */}
                <span className="break-words" title={session.userAgent ?? undefined}>
                  {shortDevice(session.userAgent) ?? t('auth.unknownDevice')}
                </span>
                {session.current && (
                  <span className="rounded-md bg-accent-soft px-1.5 py-0.5 text-xs text-ink-2">
                    {t('auth.currentSession')}
                  </span>
                )}
              </span>
              <span className="block text-xs text-muted">
                {session.ipAddress ?? t('auth.unknownAddress')} ·{' '}
                {new Intl.DateTimeFormat(i18n.language, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date(session.lastSeenAt))}
              </span>
            </span>
            {!session.current && (
              <Button size="sm" onClick={() => revoke.mutate(session.id)}>
                {t('auth.revoke')}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
